import { Link } from 'react-router-dom';
import { Notice } from '@kollez/design-system';
import { PageMeta } from '@/components/PageMeta';

/**
 * /terms — Terms of Service stub. Full text ships when Legal has finalised
 * the master agreement. For now, this route exists so footer links have a
 * real destination and enterprise procurement teams can point at it.
 */
export function TermsPage() {
  return (
    <>
      <PageMeta
        title="Terms of Service"
        description="Kollez Terms of Service. The agreement between Sonido Labs Pvt. Ltd. and the users of kollez.ai."
        canonical="/terms"
      />

      <section className="mx-auto max-w-page-narrow px-5 py-16 lg:px-10 lg:py-24 grid gap-6">
        <div>
          <p className="eyebrow">Legal</p>
          <h1 className="display text-ink-primary mt-2">Terms of Service</h1>
          <p className="mt-4 helper">Effective date: to be published on execution</p>
        </div>

        <Notice tone="info">
          The full Terms of Service are being finalised by our legal team. This page
          will host the complete text once it is executed. In the meantime, the
          summary below covers the operative parts, and you can{' '}
          <Link className="link" to="/contact?topic=terms">request the current draft</Link>{' '}
          for review.
        </Notice>

        <div className="grid gap-6 prose-read max-w-none">
          <TermsSection heading="1. Who we are">
            <p>
              Kollez is operated by <b>Sonido Labs Pvt. Ltd.</b>, an Indian company
              registered in Hyderabad, Telangana. When these terms say "we", "our" or
              "Kollez", we mean Sonido Labs.
            </p>
          </TermsSection>

          <TermsSection heading="2. Who this is for">
            <p>
              Kollez serves three audiences:
            </p>
            <ul>
              <li><b>Students</b> — use Kollez to learn skills, discover opportunities, and apply for jobs / internships / training / hackathons / mentorship. Free forever.</li>
              <li><b>Partners</b> — companies, training providers, certification bodies, mentors and hackathon hosts, who post opportunities and reach students.</li>
              <li><b>Institutions</b> — colleges, universities and education groups whose placement cells run drives on Kollez.</li>
            </ul>
          </TermsSection>

          <TermsSection heading="3. Students never pay">
            <p>
              Students never pay Kollez to use the platform, to apply for an
              opportunity, to interview, or to accept an offer. If anyone ever asks
              you to pay to receive a job through Kollez, that person is committing
              fraud —{' '}
              <Link className="link" to="/contact?topic=fraud">report them to us</Link>{' '}
              and we will act within one working day.
            </p>
          </TermsSection>

          <TermsSection heading="4. Your data">
            <p>
              Kollez processes personal data under India's Digital Personal Data
              Protection Act, 2023. You can export or delete your data anytime from
              Settings. See our{' '}
              <Link className="link" to="/privacy">Privacy Policy</Link> for details.
            </p>
          </TermsSection>

          <TermsSection heading="5. Acceptable use">
            <p>
              Kollez is not to be used to harass students, misrepresent opportunities,
              charge fees to students, harvest student PII outside the scope of a
              specific opportunity, or attempt to compromise the security of the
              platform or of other users. We reserve the right to end any partnership
              or account that breaches these rules.
            </p>
          </TermsSection>

          <TermsSection heading="6. Availability and support">
            <p>
              Kollez targets 99.9% uptime. Live incidents are posted at{' '}
              <Link className="link" to="/status">status</Link>. Support is available
              at <a className="link" href="mailto:support@kollez.ai">support@kollez.ai</a>.
            </p>
          </TermsSection>

          <TermsSection heading="7. Changes to these terms">
            <p>
              We will notify institutions and partners in advance of any material
              change. Students will be notified at sign-in. The current version is
              always at this URL.
            </p>
          </TermsSection>
        </div>

        <p className="helper text-center mt-4">
          Questions?{' '}
          <Link className="link" to="/contact?topic=terms">Get in touch with our team</Link>.
        </p>
      </section>
    </>
  );
}

function TermsSection({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-2">
      <h2 className="font-serif text-xl">{heading}</h2>
      <div className="text-ink-secondary">{children}</div>
    </section>
  );
}
